import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import { listCalls } from '../../api/callsApi';
import { formatDate, formatPhoneNumber } from '../../utils/formatters';
import useRestaurants from '../../hooks/useRestaurants';
import { useUser } from '../../hooks/useUser';

/**
 * ExportReportModal Component
 * 
 * Modal for exporting call reports as PDF.
 * Allows user to choose between last 20 calls or all calls for selected restaurant.
 */
const ExportReportModal = ({ isOpen, onClose }) => {
  const { restaurants } = useRestaurants();
  const { groupId } = useUser();
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [exportType, setExportType] = useState('last20'); // 'last20' or 'all'
  const [isExporting, setIsExporting] = useState(false);

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Build filter criteria
      const filterCriteria = {
        call_status: ['ended']
      };

      const params = {
        filter_criteria: filterCriteria,
        sort_order: 'descending',
        limit: exportType === 'last20' ? 20 : 1000, // Use high limit for "all"
      };

      // Add location filter if specific restaurant selected
      if (selectedLocationId !== 'all') {
        params.location_id = selectedLocationId;
      } else if (groupId) {
        params.group_id = groupId;
      }

      // Fetch calls
      const calls = await listCalls(params);
      
      // Get restaurant name
      const restaurant = restaurants?.find(r => r.locationId === selectedLocationId);
      const restaurantName = restaurant?.restaurantName || 'All Restaurants';

      // Generate PDF content
      generatePDF(calls, restaurantName, exportType);
      
      onClose();
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Generate PDF from calls data
  const generatePDF = (calls, restaurantName, type) => {
    // Create HTML content for PDF
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>YAPN Call Report</title>
  <style>
    @media print {
      @page { margin: 1cm; }
      body { font-family: Arial, sans-serif; font-size: 10pt; }
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 10pt;
      color: #000;
      margin: 0;
      padding: 20px;
    }
    h1 { font-size: 18pt; margin-bottom: 10px; }
    h2 { font-size: 14pt; margin-top: 20px; margin-bottom: 10px; }
    .header { margin-bottom: 20px; }
    .info { margin-bottom: 5px; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 15px;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    th {
      background-color: #f2f2f2;
      font-weight: bold;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>YAPN Dashboard - Call Records Report</h1>
    <div class="info"><strong>Location:</strong> ${restaurantName}</div>
    <div class="info"><strong>Export Type:</strong> ${type === 'last20' ? 'Last 20 Calls' : 'All Calls'}</div>
    <div class="info"><strong>Generated:</strong> ${new Date().toLocaleString()}</div>
    <div class="info"><strong>Total Calls:</strong> ${calls.length}</div>
  </div>

  <h2>Call Details</h2>
  <table>
    <thead>
      <tr>
        <th>Date/Time</th>
        <th>From</th>
        <th>To</th>
        <th>Duration</th>
        <th>Status</th>
        <th>Success</th>
        <th>Sentiment</th>
      </tr>
    </thead>
    <tbody>
      ${calls.map(call => `
        <tr>
          <td>${formatDate(call.start_timestamp)}</td>
          <td>${call.from_number ? formatPhoneNumber(call.from_number) : '—'}</td>
          <td>${call.to_number ? formatPhoneNumber(call.to_number) : '—'}</td>
          <td>${call.duration_ms ? Math.floor(call.duration_ms / 1000) + 's' : '—'}</td>
          <td>${call.call_status || '—'}</td>
          <td>${call.call_analysis?.call_successful ? 'Yes' : 'No'}</td>
          <td>${call.call_analysis?.user_sentiment || '—'}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
</body>
</html>
    `;

    // Create blob and open in new window for printing
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Clean up after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }, 500);
    };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="bg-gray-900 border-gray-700 w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Export Report</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Location
            </label>
            <select
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className="w-full h-10 rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#3AD0E6]"
            >
              <option value="all">All Locations</option>
              {restaurants?.map((restaurant) => (
                <option key={restaurant.locationId} value={restaurant.locationId}>
                  {restaurant.restaurantName || restaurant.name || `Location ${restaurant.locationId}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Export Options
            </label>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="exportType"
                  value="last20"
                  checked={exportType === 'last20'}
                  onChange={(e) => setExportType(e.target.value)}
                  className="h-4 w-4 text-[#3AD0E6] focus:ring-[#3AD0E6]"
                />
                <span className="ml-3 text-sm text-gray-300">Last 20 calls</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="exportType"
                  value="all"
                  checked={exportType === 'all'}
                  onChange={(e) => setExportType(e.target.value)}
                  className="h-4 w-4 text-[#3AD0E6] focus:ring-[#3AD0E6]"
                />
                <span className="ml-3 text-sm text-gray-300">All calls for selected location</span>
              </label>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-700"
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              className="flex-1 bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportReportModal;

